/**
 * Размеры при черчении стены — на каком расстоянии от стен её ведут.
 *
 *   • Курсор ещё не начал стену — от него до ближайших стен по четырём сторонам. Курсор на
 *     стене (Т-стык) — вдоль этой стены до её концов: «начну в 1200 от угла».
 *   • Стену ведут — от начала, если оно на стене, вдоль неё до концов; если начало в пустоте —
 *     назад, до стены, от которой отошли. От конца — вдоль стены, в которую упёрлись, или
 *     поперёк черчения: на каком расстоянии новая стена идёт от соседних параллельных.
 *
 * Начало в углу (цепочка продолжается из конца прошлой стены) не меряется: от угла и так видно.
 * Размер вдоль стены меряется по её оси, а рисуется сбоку, у грани: на оси линию закрыла бы
 * сама стена. Сбоку — с той стороны, где курсор или куда уходит новая стена.
 * Только чистые функции: рисует `DrawClearances2D`.
 */
import { add, dist, dot, mul, norm, sub, type Pt } from "@/shared/lib/geometry";
import type { WallGraph } from "@/shared/plan";
import { pointClearances, wallAxisAt, type Clearance, type WallOutline } from "@/widgets/plan";

/** Четыре стороны для точки, у которой нет направления: вправо, вниз, влево, вверх. */
const SIDES: Pt[] = [
	{ x: 1, y: 0 },
	{ x: 0, y: 1 },
	{ x: -1, y: 0 },
	{ x: 0, y: -1 },
];

/** Ближе этого, мм, к углу — точка в угол и попала: от неё не меряем. */
const NODE_SLACK = 1;

const atNode = (graph: WallGraph, point: Pt) =>
	Object.values(graph.nodes).some((node) => dist(node, point) <= NODE_SLACK);

/**
 * Размеры вдоль стены, на которой лежит точка, — в обе стороны до её концов, вынесенные к
 * грани со стороны `toward` (направление от оси); null — точка не на стене.
 */
const alongWall = (
	graph: WallGraph,
	outlines: Record<string, WallOutline>,
	point: Pt,
	toward: Pt,
	gap: number,
): Clearance[] | null => {
	const axis = wallAxisAt(graph, point);
	if (!axis) return null;
	const { along, thickness } = axis;
	const normal = { x: -along.y, y: along.x };
	const side = dot(toward, normal) < 0 ? -1 : 1;
	const shift = mul(normal, side * (thickness / 2 + gap));
	return pointClearances(point, [along, mul(along, -1)], outlines).map((clearance) => ({
		from: add(clearance.from, shift),
		to: add(clearance.to, shift),
	}));
};

/**
 * Размеры для точки черчения. `start` — начало стены, которую ведут; null — стену ещё
 * не начали, `end` — точка курсора после привязки. `pointer` — где сам курсор: с его стороны
 * стены встают размеры, пока стену не начали. `gap` — отступ размера от грани стены, мм.
 * `endAtCorner` — конец поймал угол (в том числе дотягиваемый конец соседней стены): у конца
 * размеров нет, их место занимает метка «угол».
 */
export const wallDrawClearances = (
	graph: WallGraph,
	outlines: Record<string, WallOutline>,
	start: Pt | null,
	end: Pt,
	pointer: Pt,
	gap: number,
	endAtCorner = false,
): Clearance[] => {
	if (!start) {
		if (endAtCorner || atNode(graph, end)) return [];
		return (
			alongWall(graph, outlines, end, sub(pointer, end), gap) ??
			pointClearances(end, SIDES, outlines)
		);
	}
	const length = dist(start, end);
	if (length < NODE_SLACK) return [];
	const heading = norm(sub(end, start));
	const across = { x: -heading.y, y: heading.x };
	// Начало на стене — размеры с той стороны, куда уходит новая стена; в пустоте — назад,
	// до стены, от которой отошли
	const fromStart = atNode(graph, start)
		? []
		: (alongWall(graph, outlines, start, heading, gap) ??
			pointClearances(start, [mul(heading, -1)], outlines));
	// Конец упёрся в стену — размеры со стороны, откуда пришли; в пустоте — поперёк черчения
	const fromEnd =
		endAtCorner || atNode(graph, end)
			? []
			: (alongWall(graph, outlines, end, mul(heading, -1), gap) ??
				pointClearances(end, [across, mul(across, -1)], outlines));
	return [...fromStart, ...fromEnd];
};
